import { defineAsyncComponent } from "vue";
import MetadataBase from "./MetadataBase";
import AttributeGroup from "./AttributeGroup";
import ReferenceField from "../components/xmlrdbfield/ReferenceField.vue";
import { format as formatNumber } from "../components/base/FormattedNumber.vue";
import NumberField from "../components/xmlrdbfield/NumberField.vue";
import IntField from "../components/xmlrdbfield/IntField.vue";
import RadioField from "../components/xmlrdbfield/RadioField.vue";
import DateField from "../components/xmlrdbfield/DateField.vue";
import DateTimeField from "../components/xmlrdbfield/DateTimeField.vue";
import CheckboxField from "../components/xmlrdbfield/CheckboxField.vue";
import TextareaField from "../components/xmlrdbfield/TextareaField.vue";
import TextField from "../components/xmlrdbfield/TextField.vue";
import YesNoField from "../components/xmlrdbfield/YesNoField.vue";
import OptField from "../components/xmlrdbfield/OptField.vue";
import OptRadioField from "../components/xmlrdbfield/OptRadioField.vue";
import ValidationError from "./ValidationError";
import { shallowRef } from "vue";
import AttributeValidators from "./AttributeValidators";
import { date2XMLRDB, date2german } from "../utils/date";



// Liefere eine ValidationError zurück. Falls der Parameter ein String ist, erzeuge den Error
const AsValidationError = (msg, source) => {
  return (typeof msg == "string") ?
           ValidationError.Error(msg,source):
           msg;
}

export default class Attribute extends MetadataBase {
  /* Hier sollen von aussen ValidatorFunctions registriert werden.
    Beispiel: 
    Attribute.ValidatorFunctionRegistry["MIP_MASSNAHME_FLURSTUECK.MT_ANTEILIGE_FLAECHE_M2"] = (attr) => {
      if (attr.ref.value > attr.dataElement.$.MT_FLAECHE_M2) 
         return `Anteilige Fläche (${attr.ref.value}m²) ist größer als Gesamtfläche (${attr.dataElement.$.MT_FLAECHE_M2}m²) des Flurstücks`
    }
  */
 /** @type {Record<String, import("./AttributeValidators").ValidatorFunction>} */
  static ValidatorFunctionRegistry = {...AttributeValidators};
  static AjaxReferenceField = defineAsyncComponent(() => import("../components/xmlrdbfield/AjaxReferenceField.vue")) //cyclic-reference durch async Component brechen
  static AjaxLookupFilterFunction = {};
  
  static CustomFormFields = {
    OptField,
    OptRadioField
  }
    /**
   *
   * @param {Element} node
   * @param {import("./XMLRDBNavigation").default} xmlRDBNavigation
   */
  constructor(node, xmlRDBNavigation, parentMetadata) {
    super(node, xmlRDBNavigation, parentMetadata);
    this.validators = shallowRef([]);
    const validatorTags = this.tagValuesByName("Validator");
    for (let vT of validatorTags) {
      const aValidatorFunc = Attribute.ValidatorFunctionRegistry[vT];
      if (!aValidatorFunc) {
        throw `Unbekannte Validator-Funktion '${vT}' in Attribut ${this.name()}/${parentMetadata.name()}`;
      }
      this.validators.value.push(aValidatorFunc);
    }

    // Code hier was fishy...
    // Not-Nullables müssen gesetzt sein
    // Das wird aber ausgesetzt für Werte, die nullable im Datentyp haben und Primärschlüssel sind.
    // Diese Werte sind nämlich oft Serials
    if (!this.isNullable() && this.getNodeAttribute("isIdentifying") !== "true" && this.getNodeAttribute("isReference") !== "true") {
      this.validators.value.push(Attribute.ValidatorFunctionRegistry["required"]);
    }

    if (this.isNumericType()) {
      this.validators.value.push(Attribute.ValidatorFunctionRegistry["numericMaxLength"]);
    }

    if (this.isLongText()) {
      this.validators.value.push(Attribute.ValidatorFunctionRegistry["minLength"]);
    }
  }

  // Liefert eine Function zurück, die einen filter für Ajax-Auswahllisten erzeugt.
  // Diese Function erhält als Parameter das xmlrdb-data/Attribute
  filterFunc() {
    let ajaxReferenceFilterFunctions = null;
    const ajaxReferenceFilterFunctionNames = this.tagValuesByName("ajaxReferenceFilterFunction");
    ajaxReferenceFilterFunctions = ajaxReferenceFilterFunctionNames.map(fname => {
      const fFunc = Attribute.AjaxLookupFilterFunction[fname];
      if (!fFunc) {
        throw "Unknown 'ajaxReferenceFilterFunction' " + fname;
      }
      return fFunc;
    })
    
    return (...args) => {
      return {
        and: ajaxReferenceFilterFunctions.map(f => f(...args))
      }
    }
  }

  /*
   Registriere eine eine  Validator-Funktion.
   - Validator-Funktionen erhalten ein xmlrdb-data/Attribut als Parameter
   - Validator-Funktionen liefern eine dieser Datentypen zurück:     
     a) eine Instanz von ValidationError 
     b) einen String. In diesem Fall wird davon ausgegangen, dass es sich um einen Fehler handelt und ein ValidationError um den String herum erzeugt.
     c) Ein Array von ValidationError oder String-Instanzen
     d) Kein Fehler: undefined/0/null/leeren String/false/[]

     Beispiel: 
     function MyValidator(dataAttr) {
       if (dataAttr.ref.value > 100) return "Wert darf nicht größer als 100 sein"
     }
     addValidator()
   */
  addValidator(func) {
    this.validators.value.push(func);
  }

  /*
    Liefert eine Funktion zurück, die ein (ggf. leeres) Array von ValidationError-Instzanzen zurückliefert.
    Die zurückgelieferte Funktion erhält keine Parameter. Sie arbeitet auf dem übergebenen dataAttribute
    dataAttribute: xmlrdb-data/Attribute
  */
 /**
  * 
  * @param {import("../xmlrdb-data/Attribute").default} dataAttribute 
  * @returns { ValidationError[]}
  */
  validate(dataAttribute) {
    if (this.isReadOnly(dataAttribute.dataElement)) {
      return [];
    }

    const result = [];
    // Alle Validierer abklappern
    for (let func of  this.validators.value ) {
      const valid = func(dataAttribute);  // Validierer ausführen        
      if (valid) {  // Wurde ein Problem gemeldet?
        if (Array.isArray(valid)) {
          for (let f1 of  valid ) {
            if (f1) result.push(AsValidationError(f1,dataAttribute));
          }
        }
        else {
          result.push(AsValidationError(valid,dataAttribute));
        }
      }
    }
    return result;
  }

  /**
   * Name der FormField-Komponente für den Datentyp des Attributs
   * @returns String
   */
  component() {
    let type = this.tagByName("Type")?.value() || this.getNodeAttribute("type");
    let componentName = this.tagByName("FieldComponent")?.value();
    if (componentName) {
      let c = Attribute.CustomFormFields[componentName];
      if (c) return c
      else if (!c && componentName == "YesNoField")  return YesNoField;
      else
        throw `Invalid Formfield-Component '${componentName}' for Attribute '${this.name()}'`
    }
    else if (this.getNodeAttribute("lookupClassId")) {
      if (this.isFullLookup()) {
        return ReferenceField;
      }
      else {
        return Attribute.AjaxReferenceField;
      }
    }
    else {
      if (["decimal", "float"].indexOf(type) !== -1) {
        return NumberField;
      }
      else if (["long", "int", "integer", "short"].indexOf(type) !== -1) {
        return IntField;
      }
      else if (["byte"].indexOf(type) !== -1) {
        return RadioField;
      }
      else if ("date" === type) {
        return DateField;
      }
      else if (["bool", "boolean"].indexOf(type) !== -1) {
        return CheckboxField;
      }
      else if ("long-text" === type) {
        return TextareaField;
      }
      else if ("datetime" === type) {
        return DateTimeField;
      }
      else
        return TextField;
    }
  }

  inputFieldBeforeLabel() {
    let posInputField = this.tagValueByName("posInputField");
    if (posInputField) 
      return posInputField == "beforeLabel";
    else {
      const comp = this.component();
      return (comp === CheckboxField || comp === RadioField);
    }

  }

  canUpdate() {
    return this.getNodeAttribute("canUpdate");
  }

  colorAttribute() {
    return this.getNodeAttribute("colorAttribute");
  }

  /**
   * @returns {import("./Element").default}
   */
  detailClass() {
    return this.root.getElement(this.getNodeAttribute("detailClassId"));
  }

  fkPosition() {
    return parseInt(this.getNodeAttribute("fkPosition"), 10);
  }

  fkSuccessor() {
    return this.getNodeAttribute("fkSuccessor");
  }

  isColorValue() {
    return this.getNodeAttribute("isColorValue") === "true";
  }

  isFullLookup() {
    return this.getNodeAttribute("isFullLookup") === "true";
  }

  isIdentifying() {
    return this.getNodeAttribute("isIdentifying") === "true";
  }

  isLookupLabel() {
    if (this.tagValueByName("isLookupActiveIndicator") === "true") {
      return false;
    }
    return this.getNodeAttribute("isLookupLabel") === "true";
  }

  isNullable() {
    if (this.tagByName("isNullable")) {
      return this.tagByName("isNullable").value() === "true";
    }
    return this.getNodeAttribute("nullable") === undefined || this.getNodeAttribute("nullable") === "true" || this.component() === CheckboxField;
  }

  /**
   * wertet readOnly-Tag des Attributs aus
   * Wenn isReadOnly true ist, könnte trotzdem das Programm diesen Datensatz ändern.
   *   Tag "readOnly"==true -> Programm und User können nicht ändern
   *   Tag "readOnly"==trueWithField -> Programm kann ändern User kann nicht ändern
   * Überprüft, ob ein Eingabefeld Readonly ist.
   * 
   * Sucht dazu in dem Eingabefeld und in seinen direkten Parents nach Tags "readonly". 
   * Das erste Tag, dass bei der Suche von unten nach oben in der Hierachie vorhanden
   * ist, liefert den Ergebniswert der Funktion.
   * Insbesondere könnten also "readonly=true"-Elemente Attribute mit "readonly=false" enthalten.
   * In diesem Fall wird die Funktion zurueckliefern, dass das Attribut veränderbar ist.
   * 
   * @returns bool
   */
  isReadOnly(dataElement) {
    let readOnlyTags = this.getInheritedTags("readOnly");
    if (readOnlyTags && readOnlyTags.length>0) {
      return (readOnlyTags[0].value() === "true") || (readOnlyTags[0].value() === "trueWithField");
    }
    // Prüfe ReadOnlyFunction's: Wenne eine Function true zurückliefert -> Attribuit ist Readonly
    let readOnlyFunctionTags = this.getInheritedTags("ReadOnlyFunction");
    for (let rof of readOnlyFunctionTags) {
      const readOnlyFunctionName = rof.value();
      const readOnlyFunction = MetadataBase.ConditionFunctionRegistry[readOnlyFunctionName];
      if (!readOnlyFunction) {
        throw `Unbekannte ReadOnlyFunction-Funktion '${readOnlyFunctionName}'`;
      }      
      if (readOnlyFunction(this,dataElement)) return true;
    }
    return false;
  }
  
  /**
   * wertet readOnly-Tag des Attributs aus
   * Wenn unchangable true ist kann auch das Programm diesen Datensatz nicht ändern.
   * @returns bool
   */
   isChangable() {
    let readOnlyTags = this.getInheritedTags("readOnly");
    if (readOnlyTags && readOnlyTags.length>0) {
      return (readOnlyTags[0].value() !== "true");
    }
    return true;
  }
  /**
 * @param {import("../xmlrdb-data/DataElement").default} dataElement
 * @returns {boolean}
 * @description Gibt ein Bool zurück, ob das Attribut beim Speichern mit rausgeschrieben werden soll.
 */
  doWriteOut(dataElement) {
    if (this.writeName() == "") return false;
    if (this.isIdentifying()) return true;

    const attr = dataElement.attributes[this.name()];
    const valueHasChanged =  attr.hasChanged.value;

    if (!this.isReference() && valueHasChanged) return true;
    if (dataElement.isNew.value && valueHasChanged) return true;

    if (this.tagValueByName("ForceRewrite") === "onWriteElement") return true;

    return false;
  }

  isReference() {
    return this.getNodeAttribute("isReference") === "true";
  }
  /**
   * @returns {import("./Element").default}
   */
  lookupClass() {
    return this.root.getElement(this.getNodeAttribute("lookupClassId"));
  }

  lookupClassLevel() {
    return parseInt(this.getNodeAttribute("lookupClassLevel"), 10)
  }

  maxLength() {
    return parseInt(this.tagValueByName("maxLength") ?? this.getNodeAttribute("maxLength"), 10);
  }

  minLength() {
    const minLengthTag = this.tagByName("minLength");
    if(!minLengthTag)
      return undefined;
    try {
      return parseInt(minLengthTag?.value(), 10);
    } catch (error) {
      console.error(`Fehler beim Parsen von minLength bei Attribut ${this.name()}:`, error);
    }
    return undefined;
  }

  /**
   * 
   * @returns {String}
   */
  name() {
    return this.getNodeAttribute("name");
  }

  writeName() {
    return this.tagByName("WriteAlias")?.value() ?? this.name();
  }

  precision() {
    return parseInt(this.getNodeAttribute("precision"), 10);
  }

  tagType() {
    return this.tagByName("Type")?.value() ?? this.type();
  }

  type() {
    if (this.getNodeAttribute("lookupClassId")) {
      if (this.isFullLookup()) {
        return "Reference";
      }
      else {
        return "ajaxreference";
      }
    }
    else{
      return this.getNodeAttribute("type");
    }
  }
  // Stellt CSS- Eigenschaften des InputFields zusammen.
  // Zurzeit werdena usgewertet:
  // - FieldWidth
  inputFieldCSS() {
    let result = "";
    let fw = this.tagValueByName("FieldWidth");
    if (fw) {
      result += "width:" + fw + ";";
    }
    return result;
  }

  nullValue() {
    let nullValue = this.tagByName("NullValue");
    if (nullValue) {
      return nullValue.value();
    }
    return "";
  }

  isVisible(dataElement) {
    let visible = this.tagByName("Visibility");
    if(!visible && this.parentMetadata instanceof AttributeGroup){
      visible = this.parentMetadata.tagByName("Visibility");
    }
    if (visible) {
      return visible.value() === "true";
    }

    if (!super.isVisible(dataElement)) {
      return false;
    }
    return true;
  }

  isFormVisible(dataElement) {
    let formVisibility = this.tagByName("FormVisibility");
    if (formVisibility) {
      return formVisibility.value().toLowerCase() === "true";
    }
    return this.isVisible(dataElement);
  }

  isNumericType() {
    let type = this.getNodeAttribute("type");
    return ["long", "int", "integer", "short", "byte", "decimal", "float"].indexOf(type) !== -1;
  }

  isLongText() {
    return this.tagType() === "long-text";
  }

  parseValue(val) {
    let type = this.getNodeAttribute("type");
    try {
      if (this.nullValue() === val) {
        return null;
      }
      else if (this.isNumericType()) {
        //  https://stackoverflow.com/questions/12227594/which-is-better-numberx-or-parsefloatx
        // parseFloat / parseInt erlauben Buchstaben im String. Das wollen wir nicht. Bei Number muss man nur "" abfangen.
        if (val === "") {
          return null;
        }
        let parsed = Number(val);
        if (isNaN(parsed)) {
          return val;
        }
        return parsed;
      }
      else if (["bool", "boolean"].indexOf(type) !== -1) {
        if (val === "") {
          return null;
        }
        else if (val === "0") {
          return false;
        }
        else {
          return true;
        }
      }
      else if (type === "date") {
        let parsed = new Date(val);
        if (val && !Number.isNaN(parsed.getTime()))
          return parsed
        else
          return null;
      }
      else {
        return val;
      }
    } catch (e) {
      console.error(e);
    }
    return val;
  }

  
  stringifyValue(val) {
    let type = this.getNodeAttribute("type");
    try {
      if (val === null || val === undefined) {
        return this.nullValue();
      }
      else if (this.isNumericType()) {
        return Number(val).toLocaleString('en-US', {useGrouping: false}); // Scientific Notation verhindern
      }
      else if (["bool", "boolean"].indexOf(type) !== -1) {
        if (val === null) {
          return "";
        }
        else if (val === false) {
          return "0";
        }
        else {
          return "1";
        }
      }
      else if (type === "date") {
        return date2XMLRDB(val);
      }
      else {
        return val;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  formatValue(val) {
    let type = this.getNodeAttribute("type");
    try {
      if (val === null || val === undefined) {
        return this.nullValue();
      }
      else if (["long", "int", "integer", "short", "byte"].indexOf(type) !== -1) {
        return formatNumber(val, this.tagValueByName("format") ?? "0,0");
      }
      else if (["decimal", "float"].indexOf(type) !== -1) {
        return formatNumber(val, this.tagValueByName("format") ?? "0,0");
      }
      else if (["bool", "boolean"].indexOf(type) !== -1) {
        if (val === null) {
          return "";
        }
        else if (val === false) {
          return "Falsch";
        }
        else {
          return "Wahr";
        }
      }
      else if (type === "date") {
        return date2german(val);
      }
      else {
        return val;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }
}